import { BaseStorageService, IlsJsonCreatorService, IlsModelPartsService, ReportService } from '../../services'
import {
  ImagesService,
  LlmProviderInfo,
  LlmService,
  LogAction,
  LogActionProjectionJsonDto,
  LogActionQueryJsonDto,
  LogActionService,
  ProxyService,
} from '@monorepo/golab-generated'
import {
  callChatGptKey,
  callChatLlmKey,
  CallLlmParameters,
  ChannelMessageCallChatGptAnswer,
  ChannelMessageCallChatGptRequest,
  ChannelMessageCallLlmRequest,
  ChannelMessageIframeResizeRequest,
  ChannelMessageLlmManagerAnswer,
  ChannelMessageLlmManagerRequest,
  ChannelMessageStorageRequest,
  ChannelMessageUploadImageAnswer,
  ChannelMessageUploadImageRequest,
  ChannelMessageUserManagerAnswer,
  ChannelMessageUserManagerRequest,
  GetIlsContentParameters,
  json,
  llmManagerMessageKey,
  logAccessorMessageKey,
  logActionMessageKey,
  LogActionProjectionJSON,
  LogActionQueryJSON,
  NextlabChannelMessage,
  NextlabChannelMessageLogAccessorAnswer,
  NextlabChannelMessageLogAccessorRequest,
  NextlabChannelMessageStorageAnswer,
  resizeIFrameMessageKey,
  ResourceJSON,
  ResourceQueryJSON,
  storageHandlerMessageKey,
  uploadImageKey,
  userManagerMessageKey,
} from '@monorepo/next-lab'

export class BaseNextlabMessageHandler {
  protected debug = false

  constructor(
    private readonly nextlabMessagePort: MessagePort,
    private readonly storageService: BaseStorageService,
    private readonly ilsModelPartsService: IlsModelPartsService,
    private readonly logActionService: LogActionService,
    private readonly proxyService: ProxyService,
    private readonly llmService: LlmService,
    private readonly ilsJsonCreatorService: IlsJsonCreatorService,
    private readonly imagesService: ImagesService,
    private readonly reportService: ReportService,
    private readonly iframe: HTMLIFrameElement,
    private readonly student: boolean,
  ) {
  }

  public handleMessage(channelMessage: NextlabChannelMessage): void {
    if (this.debug) {
      console.log(`IP: handle nextlab channel message`, channelMessage)
    }
    switch (channelMessage.type) {
      case logActionMessageKey:
        this.handleLogActionMessage(channelMessage.message)
        break
      case storageHandlerMessageKey:
        this.handleStorageMessage(channelMessage.message)
        break
      case logAccessorMessageKey:
        this.handleLogAccessorMessage(channelMessage.message)
        break
      case userManagerMessageKey:
        this.handleUserManagerMessage(channelMessage.message)
        break
      case resizeIFrameMessageKey:
        this.handleResizeIframeMessage(channelMessage.message)
        break
      case callChatGptKey:
        this.handleCallChatGptMessage(channelMessage.message)
        break
      case llmManagerMessageKey:
        this.handleLlmManagerMessage(channelMessage.message)
        break
      case callChatLlmKey:
        this.handleCallLlmMessage(channelMessage.message)
        break
      case uploadImageKey:
        this.handleUploadImageMessage(channelMessage.message)
        break
      default:
        console.warn(`IP: unknown nextlab channel message type`, channelMessage.type)
    }
  }

  protected handleLogActionMessage(actionMessage: json) {
    if (this.debug) {
      console.log(`IP: handle action message`, actionMessage)
    }
  }

  protected handleStorageMessage(storageMessage: ChannelMessageStorageRequest) {
    if (this.debug) {
      console.log(`IP: handle storage message`, storageMessage)
    }
    switch (storageMessage.call) {
      case 'queryResources':
        this.handleQueryResources(storageMessage)
        break
      case 'createResource':
        this.handleCreateResource(storageMessage)
        break
      case 'updateResource':
        this.handleUpdateResource(storageMessage)
        break
      case 'readResource':
        this.handleReadResource(storageMessage)
        break
      case 'resourceExists':
        this.handleResourceExists(storageMessage)
        break
      case 'deleteResource':
        this.handleDeleteResource(storageMessage)
        break
      default:
        console.warn(`unknown storage message call`, storageMessage.call)
    }
  }

  protected async handleQueryResources(request: ChannelMessageStorageRequest) {
    if (this.debug) {
      console.log(`IP: handle query resources call`, request)
    }
    const resourceQuery: ResourceQueryJSON = request.parameter as ResourceQueryJSON
    const fetchFromServer = request.fetchFromServer ?? false
    const message: NextlabChannelMessageStorageAnswer = {
      id: request.id,
      error: '',
      answer: [],
    }
    try {
      message.answer = await this.storageService.queryResourcesPromise(resourceQuery, fetchFromServer)
    } catch (error: any) {
      message.error = this.reportService.getErrorMessage(error)
    }
    this.nextlabMessagePort.postMessage({
      type: storageHandlerMessageKey,
      message: message,
    })
  }

  protected async handleCreateResource(request: ChannelMessageStorageRequest) {
    if (this.debug) {
      console.log(`IP: handle create resource call`, request)
    }
    const resourceJson: ResourceJSON = request.parameter as ResourceJSON
    const message: NextlabChannelMessageStorageAnswer = {
      id: request.id,
      error: '',
      answer: [],
    }
    try {
      message.answer = await this.storageService.createResourcePromise(resourceJson)
    } catch (error: any) {
      message.error = this.reportService.getErrorMessage(error)
    }
    this.nextlabMessagePort.postMessage({
      type: storageHandlerMessageKey,
      message: message,
    })
  }

  protected async handleUpdateResource(request: ChannelMessageStorageRequest) {
    if (this.debug) {
      console.log(`IP: handle update resource call`, request)
    }
    const resourceJson: ResourceJSON = request.parameter as ResourceJSON
    const message: NextlabChannelMessageStorageAnswer = {
      id: request.id,
      error: '',
      answer: [],
    }
    try {
      message.answer = await this.storageService.updateResourcePromise(resourceJson, request.async)
    } catch (error: any) {
      message.error = this.reportService.getErrorMessage(error)
    }
    this.nextlabMessagePort.postMessage({
      type: storageHandlerMessageKey,
      message: message,
    })
  }

  protected async handleReadResource(request: ChannelMessageStorageRequest) {
    if (this.debug) {
      console.log(`IP: handle update resource call`, request)
    }
    const resourceId: string = request.parameter as string
    const fetchFromServer = request.fetchFromServer ?? false
    const message: NextlabChannelMessageStorageAnswer = {
      id: request.id,
      error: '',
      answer: [],
    }
    try {
      message.answer = await this.storageService.readResourcePromise(resourceId, fetchFromServer)
    } catch (error: any) {
      message.error = this.reportService.getErrorMessage(error)
    }
    this.nextlabMessagePort.postMessage({
      type: storageHandlerMessageKey,
      message: message,
    })
  }

  protected async handleResourceExists(request: ChannelMessageStorageRequest) {
    if (this.debug) {
      console.log(`IP: handle exists resource call`, request)
    }
    const resourceId: string = request.parameter as string
    const fetchFromServer = request.fetchFromServer ?? false
    const message: NextlabChannelMessageStorageAnswer = {
      id: request.id,
      error: '',
      answer: [],
    }
    try {
      message.answer = await this.storageService.resourceExistsPromise(resourceId, fetchFromServer)
    } catch (error: any) {
      message.error = this.reportService.getErrorMessage(error)
    }
    this.nextlabMessagePort.postMessage({
      type: storageHandlerMessageKey,
      message: message,
    })
  }

  protected async handleDeleteResource(request: ChannelMessageStorageRequest) {
    if (this.debug) {
      console.log(`IP: handle delete resource call`, request)
    }
    const resourceId: string = request.parameter as string
    const message: NextlabChannelMessageStorageAnswer = {
      id: request.id,
      error: '',
      answer: [],
    }
    try {
      await this.storageService.deleteResourcePromise(resourceId)
    } catch (error: any) {
      message.error = this.reportService.getErrorMessage(error)
    }
    this.nextlabMessagePort.postMessage({
      type: storageHandlerMessageKey,
      message: message,
    })
  }

  protected handleLogAccessorMessage(channelMessageLogAccessorRequest: NextlabChannelMessageLogAccessorRequest) {
    if (this.debug) {
      console.log(`IP: handle log accessor message`, channelMessageLogAccessorRequest)
    }
    const query: LogActionQueryJsonDto = this.convertNextlabLogActionQueryJSONInputToLogActionQueryJson(
      channelMessageLogAccessorRequest.parameter.query,
    )
    const projection: LogActionProjectionJsonDto =
      this.convertNextlabLogActionProjectionJSONToLogActionProjectionJsonInput(
        channelMessageLogAccessorRequest.parameter.projection,
      )
    if (this.debug) {
      console.log(`IP: query`, query)
      console.log(`IP: projection`, projection)
    }
    const message: NextlabChannelMessageLogAccessorAnswer = {
      id: channelMessageLogAccessorRequest.id,
      error: '',
      answer: [],
    }
    if (this.ilsModelPartsService.published$.value && this.student) {
      this.nextlabMessagePort.postMessage({
        type: logAccessorMessageKey,
        message: message,
      })
      return
    }
    this.logActionService
      .logActionControllerRetrieveLogActions({
        body: {
          student: this.student,
          ilsId: this.ilsModelPartsService.ilsId,
          query: query,
          projection: projection,
        },
      })
      .subscribe({
        next: (logActions: LogAction[]) => {
          if (this.debug) {
            console.log(`IP: retrieved log actions`, logActions)
          }
          message.answer = logActions
          this.nextlabMessagePort.postMessage({
            type: logAccessorMessageKey,
            message: message,
          })
        },
        error: (error: any) => {
          console.error(`IP: error retrieved log actions`, error)
          message.error = this.reportService.getErrorMessage(error)
          this.nextlabMessagePort.postMessage({
            type: logAccessorMessageKey,
            message: message,
          })
        },
      })
  }

  private convertNextlabLogActionQueryJSONInputToLogActionQueryJson(query: LogActionQueryJSON): LogActionQueryJsonDto {
    const result: LogActionQueryJsonDto = {}
    if (query.actor) {
      result.actor = {}
      this.addSingleOrArrayProperty('id', query.actor.id, result.actor)
      this.addSingleOrArrayProperty('objectType', query.actor.objectType, result.actor)
    }
    if (query.target) {
      result.target = {}
      this.addSingleOrArrayProperty('objectType', query.target.objectType, result.target)
    }
    if (query.generator) {
      result.generator = {}
      this.addSingleOrArrayProperty('id', query.generator.id, result.generator)
    }
    if (query.provider) {
      result.provider = {}
      this.addSingleOrArrayProperty('inquiryPhaseId', query.provider.inquiryPhaseId, result.provider)
    }
    if (query.object) {
      result.object = {}
      this.addSingleOrArrayProperty('objectType', query.object.objectType, result.object)
    }
    if (query.verb) {
      this.addSingleOrArrayProperty('verb', query.verb, result)
    }
    if (query.published) {
      result.published = query.published
    }
    if (query.publishedServer) {
      result.publishedServer = query.publishedServer
    }
    if (query.maxResults) {
      result.maxResults = query.maxResults
    }
    if (query.pageNumber) {
      result.pageNumber = query.pageNumber
    }
    return result
  }

  private addSingleOrArrayProperty(label: string, value: string | string[] | undefined, result: json) {
    if (value) {
      result[label + 's'] = Array.isArray(value) ? value : [value]
    }
  }

  private convertNextlabLogActionProjectionJSONToLogActionProjectionJsonInput(
    projection: LogActionProjectionJSON,
  ): LogActionProjectionJsonDto {
    const result: LogActionProjectionJsonDto = {}
    if (!projection) {
      return result
    }
    if (projection.actor === true) {
      result.actorAll = true
    } else if (projection.actor) {
      result.actor = {}
      this.addSingleTrueProperty('id', projection.actor.id, result.actor)
      this.addSingleTrueProperty('displayName', projection.actor.displayName, result.actor)
      this.addSingleTrueProperty('objectType', projection.actor.objectType, result.actor)
    }
    if (projection.target === true) {
      result.targetAll = true
    } else if (projection.target) {
      result.target = {}
      this.addSingleTrueProperty('id', projection.target.id, result.target)
      this.addSingleTrueProperty('displayName', projection.target.displayName, result.target)
      this.addSingleTrueProperty('objectType', projection.target.objectType, result.target)
    }
    if (projection.generator === true) {
      result.generatorAll = true
    } else if (projection.generator) {
      result.generator = {}
      this.addSingleTrueProperty('id', projection.generator.id, result.generator)
      this.addSingleTrueProperty('displayName', projection.generator.displayName, result.generator)
      this.addSingleTrueProperty('objectType', projection.generator.objectType, result.generator)
      this.addSingleTrueProperty('url', projection.generator.url, result.generator)
    }
    if (projection.provider === true) {
      result.providerAll = true
    } else if (projection.provider) {
      result.provider = {}
      this.addSingleTrueProperty('id', projection.provider.id, result.provider)
      this.addSingleTrueProperty('displayName', projection.provider.displayName, result.provider)
      this.addSingleTrueProperty('objectType', projection.provider.objectType, result.provider)
      this.addSingleTrueProperty('url', projection.provider.url, result.provider)
      this.addSingleTrueProperty('inquiryPhase', projection.provider.inquiryPhase, result.provider)
      this.addSingleTrueProperty('inquiryPhaseId', projection.provider.inquiryPhaseId, result.provider)
      this.addSingleTrueProperty('inquiryPhaseName', projection.provider.inquiryPhaseName, result.provider)
      this.addSingleTrueProperty('ilsHasAngeLA', projection.provider.ilsHasAngeLA, result.provider)
    }
    if (projection.object === true) {
      result.objectAll = true
    } else if (projection.object) {
      result.object = {}
      this.addSingleTrueProperty('id', projection.object.id, result.object)
      this.addSingleTrueProperty('content', projection.object.content, result.object)
      this.addSingleTrueProperty('objectType', projection.object.objectType, result.object)
    }
    this.addSingleTrueProperty('verb', projection.verb, result)
    this.addSingleTrueProperty('appSequenceNumber', projection.appSequenceNumber, result)
    this.addSingleTrueProperty('sessionSequenceNumber', projection.sessionSequenceNumber, result)
    this.addSingleTrueProperty('published', projection.published, result)
    this.addSingleTrueProperty('publishedServer', projection.publishedServer, result)
    return result
  }

  private addSingleTrueProperty(label: string, value: true | undefined, result: json) {
    if (value) {
      result[label] = true
    }
  }

  protected handleUserManagerMessage(channelMessageUserManagerRequest: ChannelMessageUserManagerRequest) {
    if (this.debug) {
      console.log(`IP: handle user manager message`, channelMessageUserManagerRequest)
    }
    switch (channelMessageUserManagerRequest.call) {
      case 'createUser':
        this.handleCreateUser(channelMessageUserManagerRequest)
        break
      case 'retrieveUsers':
        this.handleRetrieveUsers(channelMessageUserManagerRequest)
        break
      default:
        console.warn(`unknown user manager message call`, channelMessageUserManagerRequest.call)
    }
  }

  protected async handleCreateUser(channelMessageUserManagerRequest: ChannelMessageUserManagerRequest) {
    if (this.debug) {
      console.log(`IP: handle create user call`, channelMessageUserManagerRequest)
    }
    const channelMessageUserManagerAnswer: ChannelMessageUserManagerAnswer = {
      id: channelMessageUserManagerRequest.id,
      error: '',
      answer: '',
    }
    if (!channelMessageUserManagerRequest.parameter) {
      channelMessageUserManagerAnswer.error = 'missing parameter'
    } else {
      const user = await this.ilsModelPartsService.createStudent(channelMessageUserManagerRequest.parameter)
      channelMessageUserManagerAnswer.answer = user.id
    }
    this.nextlabMessagePort.postMessage({
      type: userManagerMessageKey,
      message: channelMessageUserManagerAnswer,
    })
  }

  protected async handleRetrieveUsers(channelMessageUserManagerRequest: ChannelMessageUserManagerRequest) {
    if (this.debug) {
      console.log(`IP: handle retrieve user call`, channelMessageUserManagerRequest)
    }
    const users = await this.ilsModelPartsService.getStudents()
    this.nextlabMessagePort.postMessage({
      type: userManagerMessageKey,
      message: {
        id: channelMessageUserManagerRequest.id,
        error: '',
        answer: users,
      },
    })
  }

  protected handleResizeIframeMessage(channelMessageIframeResizeRequest: ChannelMessageIframeResizeRequest) {
    if (channelMessageIframeResizeRequest.desiredHeight > 20) {
      this.iframe.style.height = channelMessageIframeResizeRequest.desiredHeight + 'px'
    } else {
      // console.log(`IP: invalid desired height`, channelMessageIframeResizeRequest.desiredHeight)
    }
  }

  protected handleCallChatGptMessage(channelMessageCallChatGptRequest: ChannelMessageCallChatGptRequest) {
    if (this.debug) {
      console.log(`IP: handle call chat gpt call`, channelMessageCallChatGptRequest)
    }
    const sendAnswer = (error: any, answer: string) => {
      const message: ChannelMessageCallChatGptAnswer = {
        id: channelMessageCallChatGptRequest.id,
        error: '',
        answer: '',
      }
      if (error) {
        message.error = this.reportService.getErrorMessage(error)
      } else {
        message.answer = answer
      }
      this.nextlabMessagePort.postMessage({
        type: callChatGptKey,
        message: message,
      })
    }
    try {
      this.proxyService
        .proxyControllerCallChatGptApi({
          ilsId: this.ilsModelPartsService.ilsId,
          body: {
            model: channelMessageCallChatGptRequest.model,
            systemRole: channelMessageCallChatGptRequest.systemRole,
            temperature: channelMessageCallChatGptRequest.temperature,
            userQuestion: channelMessageCallChatGptRequest.userQuestion,
          },
        })
        .subscribe({
          next: (response) => {
            sendAnswer(null, response.response)
          },
          error: (error) => {
            sendAnswer(error, '')
          },
        })
    } catch (error: any) {
      sendAnswer(error, '')
    }
  }

  protected handleCallLlmMessage(channelMessageCallLlmRequest: ChannelMessageCallLlmRequest) {
    if (this.debug) {
      console.log(`IP: handle call chat llm call`, channelMessageCallLlmRequest)
    }
    const sendAnswer = (error: any, answer: string) => {
      const message: ChannelMessageCallChatGptAnswer = {
        id: channelMessageCallLlmRequest.id,
        error: '',
        answer: '',
      }
      if (error) {
        message.error = this.reportService.getErrorMessage(error)
      } else {
        message.answer = answer
      }
      this.nextlabMessagePort.postMessage({
        type: callChatLlmKey,
        message: message,
      })
    }
    try {
      this.llmService
        .llmControllerChatLlm({
          ilsId: this.ilsModelPartsService.ilsId,
          body: {
            provider: channelMessageCallLlmRequest.provider,
            model: channelMessageCallLlmRequest.model,
            // systemRole: channelMessageCallLlmRequest.systemRole,
            temperature: channelMessageCallLlmRequest.temperature,
            prompt: channelMessageCallLlmRequest.prompt,
            messages: channelMessageCallLlmRequest.messages,
          },
        })
        .subscribe({
          next: (response) => {
            sendAnswer(null, response.response)
          },
          error: (error) => {
            sendAnswer(error, '')
          },
        })
    } catch (error: any) {
      sendAnswer(error, '')
    }
  }

  protected handleLlmManagerMessage(channelMessageLlmManagerRequest: ChannelMessageLlmManagerRequest) {
    if (this.debug) {
      console.log(`IP: handle llm manager message`, channelMessageLlmManagerRequest)
    }
    switch (channelMessageLlmManagerRequest.call) {
      case 'listProviders':
        this.handleListProviders(channelMessageLlmManagerRequest)
        break
      case 'callLlm':
        this.handleCallLlm(channelMessageLlmManagerRequest)
        break
      case 'getIlsContent':
        this.handleGetIlsContent(channelMessageLlmManagerRequest)
        break
      default:
        console.warn(`unknown llm manager message call`, channelMessageLlmManagerRequest.call)
    }
  }

  protected handleListProviders(channelMessageLlmManagerRequest: ChannelMessageLlmManagerRequest) {
    if (this.debug) {
      console.log(`IP: handle list providers`, channelMessageLlmManagerRequest)
    }
    const sendAnswer = (error: any, answer: LlmProviderInfo[]) => {
      const message: ChannelMessageLlmManagerAnswer = {
        id: channelMessageLlmManagerRequest.id,
        error: '',
        answer: [],
      }
      if (error) {
        message.error = this.reportService.getErrorMessage(error)
      } else {
        message.answer = answer
      }
      this.nextlabMessagePort.postMessage({
        type: llmManagerMessageKey,
        message: message,
      })
    }
    try {
      this.llmService.llmControllerGetProviderList().subscribe({
        next: (response) => {
          sendAnswer(null, response)
        },
        error: (error) => {
          sendAnswer(error, [])
        },
      })
    } catch (error: any) {
      sendAnswer(error, [])
    }
  }

  protected handleCallLlm(channelMessageLlmManagerRequest: ChannelMessageLlmManagerRequest) {
    if (this.debug) {
      console.log(`IP: handle call llm`, channelMessageLlmManagerRequest)
    }
    const sendAnswer = (error: any, answer: string) => {
      const message: ChannelMessageLlmManagerAnswer = {
        id: channelMessageLlmManagerRequest.id,
        error: '',
        answer: '',
      }
      if (error) {
        message.error = this.reportService.getErrorMessage(error)
      } else {
        message.answer = answer
      }
      this.nextlabMessagePort.postMessage({
        type: llmManagerMessageKey,
        message: message,
      })
    }
    try {
      if (!channelMessageLlmManagerRequest.parameter) {
        sendAnswer('missing parameter', '')
      }
      const callParameters = channelMessageLlmManagerRequest.parameter! as CallLlmParameters
      this.llmService
        .llmControllerChatLlm({
          ilsId: this.ilsModelPartsService.ilsId,
          body: {
            provider: callParameters.provider,
            model: callParameters.model,
            // systemRole: callParameters.systemRole,
            temperature: callParameters.temperature,
            prompt: callParameters.prompt,
            messages: callParameters.messages,
          },
        })
        .subscribe({
          next: (response) => {
            sendAnswer(null, response.response)
          },
          error: (error) => {
            sendAnswer(error, '')
          },
        })
    } catch (error: any) {
      sendAnswer(error, '')
    }
  }

  protected handleGetIlsContent(channelMessageLlmManagerRequest: ChannelMessageLlmManagerRequest) {
    if (this.debug) {
      console.log(`IP: handle get ILS content`, channelMessageLlmManagerRequest)
    }
    const sendAnswer = (error: any, answer: string) => {
      const message: ChannelMessageLlmManagerAnswer = {
        id: channelMessageLlmManagerRequest.id,
        error: '',
        answer: '',
      }
      if (error) {
        message.error = this.reportService.getErrorMessage(error)
      } else {
        message.answer = answer
      }
      this.nextlabMessagePort.postMessage({
        type: llmManagerMessageKey,
        message: message,
      })
    }
    try {
      const upToGeneratorId = channelMessageLlmManagerRequest.parameter ?
        (channelMessageLlmManagerRequest.parameter as GetIlsContentParameters).upToGeneratorId : undefined
      this.ilsJsonCreatorService.createIlsJson({upToGeneratorId}).then(ilsJson => {
        sendAnswer(null, JSON.stringify(ilsJson))
      }).catch(error => {
        sendAnswer(error, '')
      })
    } catch (error: any) {
      sendAnswer(error, '')
    }
  }

  protected handleUploadImageMessage(channelMessageUploadImageRequest: ChannelMessageUploadImageRequest) {
    if (this.debug) {
      console.log(`IP: handle upload image call`, channelMessageUploadImageRequest)
    }
    const sendAnswer = (error: any, answer: string) => {
      const message: ChannelMessageUploadImageAnswer = {
        id: channelMessageUploadImageRequest.id,
        error: '',
        answer: '',
      }
      if (error) {
        message.error = this.reportService.getErrorMessage(error)
      } else {
        message.answer = answer
      }
      this.nextlabMessagePort.postMessage({
        type: uploadImageKey,
        message: message,
      })
    }
    try {
      this.imagesService
        .imagesControllerUploadFileBase64({
          body: {
            imageString: channelMessageUploadImageRequest.imageData,
          },
        })
        .subscribe({
          next: (response) => {
            if (this.debug) {
              console.log(`IP: uploaded image`, response)
            }
            sendAnswer(null, response.path)
          },
          error: (error) => {
            console.error(`IP: error uploading image`, error)
            sendAnswer(error, '')
          },
        })
    } catch (error: any) {
      sendAnswer(error, '')
    }
  }
}
